import {
	Box,
	Button,
	Grid,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Skeleton,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, FormEvent, MouseEvent, useMemo, useRef, useState } from "react";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import "./authorizedList.css";
import {
	DataGrid,
	GridColDef,
	GridPagination,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowHeightParams,
	GridRowSelectionModel
} from "@mui/x-data-grid";
import { ROWS_PER_PAGE, TIME_FILTER_FORMAT, VEHICLE_TYPE_E, VEHICLE_TYPES_LIST } from "../../../utils/constants";
import {
	CombinedRowType,
	IGetAuthorizedListRequestData,
	IGetAuthorizedListResponseData,
	IUploadAuthorizedVehicleExcelOwnerDetailsOld,
	IUploadAuthorizedVehiclesExcelResponseData,
	IUploadResponse,
	IUploadResponseData,
	IUploadResponseWhitelistDetails
} from "../../../interfaces";
import {
	useGetAuthorizedListQuery,
	useGetVehiclesListQuery,
	useUploadAuthorizedVehiclesDataMutation
} from "../../../redux/reducers/dataManagement.reducers";
import { useGetOwnerTypesQuery, useGetSiteConfigQuery } from "../../../redux/reducers/settings.reducer";
import AuthorizedMemberDetailsDialog from "./AuthorizedMemberDetailsDialog";
import downloadServices from "../../../redux/services/download.services";
import { LoadingButton } from "@mui/lab";
import TablePagination from "../../../common/TablePagination";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../../utils/commonUtils";
import NewUploadDataDialog from "../../../common/NewUploadDataDialog";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";

export interface IAuthorizedVehicleDetails {
	id: number;
	vehicle_number: string;
	vehicle_type: number;
}

export interface IAuthorizedMemberDetails {
	id: number;
	sr_no: number;
	name: string;
	flat_no: string;
	owner_type: string;
	owner_type_id: number;
	contact_number: string;
	validity_start_date: string | null;
	validity_end_date: string | null;
	vehicles: IAuthorizedVehicleDetails[];
	is_custom_flat: boolean;
}

interface IDataManagementCardsData {
	title: string;
	value: string | number;
	icon: React.ReactNode;
	loading: boolean;
}

const AuthorizedListPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const AuthorizedList: FC = () => {
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const searchInputRef = useRef<HTMLInputElement>(null);

	const [searchInput, setSearchInput] = useState<string>("");
	const [filters, setFilters] = useState<IGetAuthorizedListRequestData>({ page: 1 });
	const [selectedWhitelistIds, setSelectedWhitelistIds] = useState<number[]>([]);
	const [showAuthorizedMemberDetailsDialog, setShowAuthorizedMemberDetailsDialog] = useState<boolean>(false);
	const [selectedMemberDetails, setSelectedMemberDetails] = useState<IAuthorizedMemberDetails | null>(null);
	const [showDownloadButtonLoader, setShowDownloadButtonLoader] = useState<boolean>(false);
	const [showUploadDataDialog, setShowUploadDataDialog] = useState<boolean>(false);
	const [uploadDataLoading, setUploadDataLoading] = useState<boolean>(false);

	const {
		data: authorizedListData,
		isLoading: getAuthorizedListLoading,
		isFetching: getAuthorizedListFetching,
		refetch: refetchAuthorizedList
	} = useGetAuthorizedListQuery(filters);

	const [uploadAuthorizedVehiclesExcel] = useUploadAuthorizedVehiclesDataMutation();

	const { data: getSiteConfigResponse } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const {
		data: getOwnerTypesResponse,
		isLoading: getOwnerTypesLoading,
		isFetching: getOwnerTypesFetching
	} = useGetOwnerTypesQuery();
	const ownerTypesList = useMemo(() => getOwnerTypesResponse?.results ?? [], [getOwnerTypesResponse?.results]);

	const tenantOwnerTypeDetails = ownerTypesList?.find((item) => item.type === "Tenant");
	const memberOwnerTypeDetails = ownerTypesList?.find((item) => item.type === "Member");

	const {
		data: allOwnersData,
		isLoading: allOwnersLoading,
		isFetching: allOwnersFetching,
		refetch: refetchAllOwnersData
	} = useGetAuthorizedListQuery({});

	const {
		data: tenantsData,
		isLoading: tenantsLoading,
		isFetching: tenantsFetching,
		refetch: refetchTenantsData
	} = useGetAuthorizedListQuery({ owner_type: tenantOwnerTypeDetails?.id }, { skip: !tenantOwnerTypeDetails });

	const {
		data: membersData,
		isLoading: membersLoading,
		isFetching: membersFetching,
		refetch: refetchMembersData
	} = useGetAuthorizedListQuery({ owner_type: memberOwnerTypeDetails?.id }, { skip: !memberOwnerTypeDetails });

	const {
		data: vehiclesList,
		isLoading: getVehiclesListLoading,
		isFetching: getVehiclesListFetching,
		refetch: refetchVehiclesList
	} = useGetVehiclesListQuery();

	const [ownerTypeDropdownAnchorElement, setOwnerTypeDropdownAnchorElement] = useState<HTMLElement | null>(null);
	const openOwnerTypeDropdown = Boolean(ownerTypeDropdownAnchorElement);

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(getSiteConfigResponse?.cloud_sync_enabled ?? true),
		[getSiteConfigResponse]
	);

	const cardsData = useMemo<IDataManagementCardsData[]>(
		() => [
			{
				title: "Registered Vehicles",
				value: Intl.NumberFormat("en-IN").format(vehiclesList?.count ?? 0),
				icon: <DirectionsCarFilledIcon color="success" sx={{ fontSize: { xs: "24px", sm: "28px" } }} />,
				loading: getVehiclesListLoading || getVehiclesListFetching
			},
			{
				title: "Members",
				value: Intl.NumberFormat("en-IN").format(
					ownerTypesList && ownerTypesList.length > 0 ? membersData?.count ?? 0 : 0
				),
				icon: <SupervisedUserCircleIcon color="success" sx={{ fontSize: { xs: "24px", sm: "28px" } }} />,
				loading: getOwnerTypesLoading || getOwnerTypesFetching || membersLoading || membersFetching
			},
			{
				title: "Tenants",
				value: Intl.NumberFormat("en-IN").format(
					ownerTypesList && ownerTypesList.length > 0 ? tenantsData?.count ?? 0 : 0
				),
				icon: <HowToRegIcon color="success" sx={{ fontSize: { xs: "24px", sm: "28px" } }} />,
				loading: getOwnerTypesLoading || getOwnerTypesFetching || tenantsLoading || tenantsFetching
			},
			{
				title: "Miscellaneous",
				value: Intl.NumberFormat("en-IN").format(
					ownerTypesList && ownerTypesList.length > 0
						? (allOwnersData?.count ?? 0) - ((membersData?.count ?? 0) + (tenantsData?.count ?? 0))
						: 0
				),
				icon: <AssignmentTurnedInIcon color="success" sx={{ fontSize: { xs: "24px", sm: "28px" } }} />,
				loading:
					getOwnerTypesLoading ||
					getOwnerTypesFetching ||
					allOwnersLoading ||
					allOwnersFetching ||
					membersLoading ||
					membersFetching ||
					tenantsLoading ||
					tenantsFetching
			}
		],
		[
			allOwnersData?.count,
			allOwnersFetching,
			allOwnersLoading,
			getOwnerTypesFetching,
			getOwnerTypesLoading,
			getVehiclesListFetching,
			getVehiclesListLoading,
			membersData?.count,
			membersFetching,
			membersLoading,
			ownerTypesList,
			tenantsData?.count,
			tenantsFetching,
			tenantsLoading,
			vehiclesList?.count
		]
	);

	function refetchAllData() {
		refetchAuthorizedList();
		refetchTenantsData();
		refetchMembersData();
		refetchVehiclesList();
		refetchAllOwnersData();
	}

	function handleOpenAuthorizedMemberDetailsDialog(memberDetails: IAuthorizedMemberDetails | null) {
		setSelectedMemberDetails(memberDetails);
		setShowAuthorizedMemberDetailsDialog(true);
	}

	function handleCloseAuthorizedMemberDetailsDialog() {
		setShowAuthorizedMemberDetailsDialog(false);
		setSelectedMemberDetails(null);
		refetchAllData();
	}

	function handleOpenUploadDataDialog() {
		setShowUploadDataDialog(true);
	}

	function handleCloseUploadDataDialog() {
		setShowUploadDataDialog(false);
		refetchAllData();
		setUploadDataLoading(false);
	}

	function handleChangeSearchInput(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		setSearchInput(event.target.value);
	}

	function handleClearSearchInput() {
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };
			delete updatedFilters.search;
			return updatedFilters;
		});

		setSearchInput("");

		if (searchInputRef.current) searchInputRef.current.focus();
	}

	function handleSearchAuthorizedListVehicle(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function handleSelectRows(selectedRows: GridRowSelectionModel) {
		setSelectedWhitelistIds(selectedRows as number[]);
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
	}

	function getOwnerTypeName(ownerTypeId: number) {
		return ownerTypesList?.find((ownerTypeItem) => ownerTypeItem.id === ownerTypeId)?.type ?? "Owner Type";
	}

	function handleOpenOwnerTypeDropdown(event: MouseEvent<HTMLDivElement>) {
		setOwnerTypeDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseOwnerTypeDropdown() {
		setOwnerTypeDropdownAnchorElement(null);
	}

	function handleChangeSelectedOwnerType(updatedValue: number) {
		handleCloseOwnerTypeDropdown();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (updatedValue >= 0) updatedFilters.owner_type = updatedValue;
			else delete updatedFilters.owner_type;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function handleDownloadButtonClick() {
		setShowDownloadButtonLoader(true);

		downloadServices
			.handleDownloadAuthorizedList(filters)
			.catch((error) => {
				console.error("Download authorized list failed ::", error);
			})
			.finally(() => {
				setShowDownloadButtonLoader(false);
			});
	}

	function handleUploadAuthorizedListDetails(
		rowsData: CombinedRowType[],
		isCustomFlatNumber: boolean,
		isVerified?: boolean
	): Promise<IUploadResponse> {
		return new Promise((resolve) => {
			setUploadDataLoading(true);

			const uploadData: IUploadAuthorizedVehicleExcelOwnerDetailsOld[] = [];

			for (const rowDetails of rowsData) {
				if (rowDetails.is_deleted) continue;

				if (rowDetails.errorFields && Object.keys(rowDetails.errorFields).length > 0) {
					continue;
				}

				const vehicleTypeDetails = VEHICLE_TYPES_LIST.find(
					(vehicleTypeItem) => vehicleTypeItem.name.toLowerCase() === rowDetails["Vehicle Type"].toLowerCase()
				);

				const ownerTypeDetails = ownerTypesList.find(
					(ownerTypeItem) => ownerTypeItem.type.toLowerCase() === rowDetails["Owner Type"].toLowerCase()
				);

				const uploadDataItem: IUploadAuthorizedVehicleExcelOwnerDetailsOld = {
					auth_type: "whitelist",
					comment: null,
					number_plate: rowDetails["Number Plate"],
					owner: {
						flat_number: rowDetails["Flat Number"] ? rowDetails["Flat Number"] : "",
						is_valid: false,
						name: rowDetails["Vehicle Owner Name"] ? rowDetails["Vehicle Owner Name"] : "",
						owner_type: ownerTypeDetails?.id ?? 0,
						phone_number: rowDetails["Contact Number"] ? rowDetails["Contact Number"] : ""
					},
					vehicle_type: vehicleTypeDetails?.id ?? VEHICLE_TYPE_E.FOUR_WHEELER
				};

				if (ownerTypeDetails && ownerTypeDetails.has_expiry) {
					const validityStartDate = moment(rowDetails["Validity Start Date"]);
					const validityEndDate = moment(rowDetails["Validity End Date"]);

					const validityStartDateString = validityStartDate.format(TIME_FILTER_FORMAT);
					const validityEndDateString = validityEndDate.format(TIME_FILTER_FORMAT);

					const isValid =
						validityStartDate.isSameOrBefore(moment(), "day") && validityEndDate.isSameOrAfter(moment(), "day");

					uploadDataItem.owner.validity_start_date = validityStartDateString;
					uploadDataItem.owner.validity_end_date = validityEndDateString;

					uploadDataItem.validity_start_date = validityStartDateString;
					uploadDataItem.validity_end_date = validityEndDateString;
					uploadDataItem.owner.is_valid = isValid;
				}

				uploadData.push(uploadDataItem);
			}

			if (uploadData.length <= 0) {
				resolve({
					data: [],
					message: ""
				});
				return;
			}

			uploadAuthorizedVehiclesExcel({
				data: uploadData,
				is_verified: isVerified ?? false,
				is_custom_flat_number: isCustomFlatNumber
			})
				.unwrap()
				.then((response) => {
					resolve({
						data: [],
						message: response.message
					});
					dispatch(
						showFeedbackNotification({
							message: response.message,
							severity: "success"
						})
					);
				})
				.catch((error) => {
					if (!error.data) {
						resolve({
							data: [],
							message: "Something went wrong"
						});
						return;
					}

					const errorData = error.data as IUploadAuthorizedVehiclesExcelResponseData;

					dispatch(
						showFeedbackNotification({
							message: errorData.message,
							severity: "error"
						})
					);

					const returnData: IUploadResponseWhitelistDetails[] = [];

					if (errorData.data) {
						/**
						 * GET ALL THE ROWS WITH ERRORS (ROW OBJECTS THAT HAVE THE database_data KEY)
						 */
						const filteredErrorData = errorData.data.filter(
							(item) =>
								(!!item.database_data && Object.keys(item.database_data).length > 0) ||
								(!!item.excel_data && Object.keys(item.excel_data).length > 0)
						);

						for (const errorDataItem of filteredErrorData) {
							if (!errorDataItem.remarks) continue;

							const responseErrorData = errorDataItem.database_data ?? errorDataItem.excel_data ?? {};

							if (errorDataItem.remarks === "Owner Duplicate") {
								const dbOwnerData = {
									name: responseErrorData.owner?.name ?? "",
									flatNumber: responseErrorData.owner?.flat_number ?? "",
									ownerType: responseErrorData.owner?.owner_type ?? -1,
									contactNumber: responseErrorData.owner?.phone_number ?? "",
									validityStartDate:
										responseErrorData.owner && responseErrorData.owner.validity_start_date
											? moment(responseErrorData.owner.validity_start_date)
											: null,
									validityEndDate:
										responseErrorData.owner && responseErrorData.owner.validity_end_date
											? moment(responseErrorData.owner.validity_end_date)
											: null
								};

								const excelOwnerData = {
									name: errorDataItem.name,
									flatNumber: errorDataItem.flat_number ?? "",
									ownerType: errorDataItem.owner_type ?? -1,
									contactNumber: errorDataItem.phone_number ?? "",
									validityStartDate: errorDataItem.validity_start_date
										? moment(errorDataItem.validity_start_date)
										: null,
									validityEndDate: errorDataItem.validity_end_date ? moment(errorDataItem.validity_end_date) : null
								};

								if (JSON.stringify(dbOwnerData) === JSON.stringify(excelOwnerData)) {
									continue;
								}

								const ownerTypeDetails = ownerTypesList.find(
									(ownerTypeItem) => ownerTypeItem.id === (responseErrorData.owner?.id ?? -1)
								);

								let validityStartDate: Date | null = null;
								let validityEndDate: Date | null = null;

								if (
									responseErrorData.owner &&
									responseErrorData.owner.validity_start_date &&
									responseErrorData.owner.validity_end_date
								) {
									validityStartDate = new Date(responseErrorData.owner.validity_start_date);
									validityEndDate = new Date(responseErrorData.owner.validity_end_date);
								}

								const returnDataItem: IUploadResponseWhitelistDetails = {
									"Vehicle Owner Name": responseErrorData.owner?.name ?? "",
									"Flat Number": responseErrorData.owner?.flat_number ?? "",
									"Number Plate": "",
									"Vehicle Type": "",
									"Owner Type": ownerTypeDetails?.type ?? "",
									"Contact Number": responseErrorData.owner?.phone_number ?? "",
									"Validity Start Date": validityStartDate,
									"Validity End Date": validityEndDate,
									id: 0,
									sr_no: 0,
									is_deleted: false,
									remarks: errorDataItem.remarks,
									errorFields: {}
								};

								if (returnDataItem.errorFields) {
									if (responseErrorData.owner && responseErrorData.owner.remarks) {
										returnDataItem.errorFields["Vehicle Owner Name"] = [responseErrorData.owner.remarks];
									} else {
										returnDataItem.errorFields["Vehicle Owner Name"] = ["This owner already exists"];
									}
								}

								returnData.push(returnDataItem);
							} else if (
								errorDataItem.remarks === "Vehicle Duplicates" ||
								errorDataItem.remarks === "Vehicle and Owner Both Duplicate"
							) {
								const ownerTypeDetails = ownerTypesList.find(
									(ownerTypeItem) => ownerTypeItem.id === responseErrorData.owner?.owner_type || -1
								);

								let validityStartDate: Date | null = null;
								let validityEndDate: Date | null = null;

								if (
									responseErrorData.owner &&
									responseErrorData.owner.validity_start_date &&
									responseErrorData.owner.validity_end_date
								) {
									validityStartDate = new Date(responseErrorData.owner.validity_start_date);
									validityEndDate = new Date(responseErrorData.owner.validity_end_date);
								}

								if (responseErrorData.vehicle) {
									for (const vehicleItem of responseErrorData.vehicle) {
										const vehicleTypeDetails = VEHICLE_TYPES_LIST.find(
											(vehicleTypeItem) => vehicleTypeItem.id === vehicleItem.vehicle_type || -1
										);

										const returnDataItem: IUploadResponseWhitelistDetails = {
											"Vehicle Owner Name": responseErrorData.owner?.name ?? "",
											"Flat Number": responseErrorData.owner?.flat_number ?? "",
											"Number Plate": vehicleItem.number_plate ?? "",
											"Vehicle Type": vehicleTypeDetails?.name ?? "",
											"Owner Type": ownerTypeDetails?.type ?? "",
											"Contact Number": responseErrorData.owner?.phone_number ?? "",
											"Validity Start Date": validityStartDate,
											"Validity End Date": validityEndDate,
											id: 0,
											sr_no: 0,
											is_deleted: false,
											remarks: errorDataItem.remarks,
											errorFields: {}
										};

										if (returnDataItem.errorFields) {
											if (vehicleItem.remarks) {
												returnDataItem.errorFields["Number Plate"] = [vehicleItem.remarks];
											} else {
												returnDataItem.errorFields["Number Plate"] = ["Vehicle with this number already exists"];
											}

											if (responseErrorData.owner && responseErrorData.owner.remarks) {
												returnDataItem.errorFields["Vehicle Owner Name"] = [responseErrorData.owner.remarks];
											} else if (errorDataItem.remarks === "Vehicle and Owner Both Duplicate") {
												returnDataItem.errorFields["Vehicle Owner Name"] = ["This owner already exists"];
											}
										}

										returnData.push(returnDataItem);
									}
								}
							}
						}
					}

					resolve({
						data: returnData as IUploadResponseData[],
						message: errorData.message
					});
				})
				.finally(() => {
					setUploadDataLoading(false);
				});
		});
	}

	function getAuthorizedListRowData(authorizedList: IGetAuthorizedListResponseData[]): IAuthorizedMemberDetails[] {
		const activePage = filters.page ? filters.page - 1 : 0;

		return authorizedList.map((authorizedListItem, index) => ({
			id: authorizedListItem.id,
			sr_no: activePage * ROWS_PER_PAGE + index + 1,
			name: authorizedListItem.name,
			flat_no: authorizedListItem.flat_number,
			owner_type: authorizedListItem.owner_type.type_of,
			owner_type_id: authorizedListItem.owner_type.id,
			contact_number: authorizedListItem.phone_number,
			validity_start_date: authorizedListItem.validity_start_date,
			validity_end_date: authorizedListItem.validity_end_date,
			vehicles: authorizedListItem.vehicles.map((vehicleItem) => ({
				id: vehicleItem.id,
				vehicle_number: vehicleItem.number_plate,
				vehicle_type: vehicleItem.vehicle_type
			})),
			is_custom_flat: authorizedListItem.is_custom_flat_number
		}));
	}

	function renderOwnerTypeDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper align-center"
					id="owner-type-dropdown"
					aria-controls={openOwnerTypeDropdown ? "owner-type-dropdown-menu" : undefined}
					aria-haspopup="true"
					aria-expanded={openOwnerTypeDropdown ? "true" : undefined}
					onClick={handleOpenOwnerTypeDropdown}
				>
					<Typography variant="subtitle2" textTransform="capitalize">
						{filters.owner_type ? getOwnerTypeName(filters.owner_type) : "Owner Type"}
					</Typography>
					{openOwnerTypeDropdown ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="owner-type-dropdown-menu"
					anchorEl={ownerTypeDropdownAnchorElement}
					open={openOwnerTypeDropdown}
					onClose={handleCloseOwnerTypeDropdown}
					MenuListProps={{ "aria-labelledby": "owner-type-dropdown" }}
					sx={{ width: "100%" }}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					transformOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<MenuItem onClick={() => handleChangeSelectedOwnerType(-1)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{ownerTypesList
						? ownerTypesList.map((ownerTypeItem) => (
								<MenuItem key={ownerTypeItem.id} onClick={() => handleChangeSelectedOwnerType(ownerTypeItem.id)}>
									{ownerTypeItem.type}
								</MenuItem>
						  ))
						: null}
				</Menu>
			</Box>
		);
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 100,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "name",
			headerName: "Name",
			flex: 1,
			minWidth: 150,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "flat_no",
			headerName: "Unit No.",
			flex: 1,
			minWidth: 100,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "vehicle_number",
			headerName: "Vehicle Number",
			flex: 1,
			minWidth: 150,
			sortable: false,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<IAuthorizedMemberDetails>) => (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					{params.row.vehicles.map((vehicleItem) => (
						<Box key={vehicleItem.id} sx={{ flex: 1, display: "flex", alignItems: "center" }}>
							<Typography variant="body2">{vehicleItem.vehicle_number}</Typography>
						</Box>
					))}
				</Box>
			)
		},
		{
			field: "vehicle_type",
			headerName: "Vehicle Type",
			flex: 1,
			minWidth: 150,
			sortable: false,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<IAuthorizedMemberDetails>) => (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					{params.row.vehicles.map((vehicleItem) => (
						<Box key={vehicleItem.id} sx={{ flex: 1, display: "flex", alignItems: "center" }}>
							<Typography variant="body2">
								{vehicleItem.vehicle_type === 0 ? "Four Wheeler" : vehicleItem.vehicle_type === 1 ? "Two Wheeler" : ""}
							</Typography>
						</Box>
					))}
				</Box>
			)
		},
		{
			field: "owner_type",
			headerName: "Owner Type",
			flex: 1,
			minWidth: 120,
			sortable: false,
			renderHeader: renderOwnerTypeDropdown,
			align: "center"
		},
		{
			field: "contact_number",
			headerName: "Contact Number",
			flex: 1,
			minWidth: 200,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IAuthorizedMemberDetails>) => (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
					<IconButton size="small" onClick={() => handleOpenAuthorizedMemberDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" sx={{ color: "#1e53af" }} />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box
			className="authorized-list-screen-wrapper"
			sx={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				gap: { xs: "0.75rem", sm: "1rem" },
				// Make the page scrollable on mobile instead of just the DataGrid
				overflow: { xs: "auto", sm: "hidden" },
				height: { xs: "100vh", sm: "auto" },
				paddingBottom: { xs: "1rem", sm: 0 }
			}}
		>
			<Grid container spacing={{ xs: 1, sm: 2 }}>
				{cardsData.map((cardItem, index) => (
					<Grid item xs={6} lg={3} key={index}>
						{cardItem.loading ? (
							<Skeleton
								variant="rounded"
								animation="wave"
								sx={{
									height: { xs: "100px", sm: "120px" },
									borderRadius: "12px"
								}}
							/>
						) : (
							<Box
								className="authorized-list-data-card"
								sx={{
									background: "#ffffff",
									padding: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
									display: "flex",
									alignItems: "center",
									gap: { xs: "0.75rem", sm: "1rem" },
									borderRadius: "12px",
									boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
									border: "1px solid rgba(0, 0, 0, 0.06)",
									transition: "all 0.3s ease",
									position: "relative",
									overflow: "hidden",
									height: { xs: "100px", sm: "auto" }
								}}
							>
								<Box
									className="authorized-list-data-card-icon"
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										padding: { xs: "0.5rem", sm: "0.75rem" },
										background: "#e8f5e8",
										borderRadius: "10px",
										minWidth: { xs: "40px", sm: "48px" },
										height: { xs: "40px", sm: "48px" },
										flexShrink: 0
									}}
								>
									{cardItem.icon}
								</Box>

								<Box
									className="authorized-list-data-card-details"
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: "0.25rem",
										flex: 1,
										minWidth: 0
									}}
								>
									<Typography
										variant="body2"
										color="#00000080"
										sx={{
											fontSize: { xs: "0.75rem", sm: "0.875rem" },
											lineHeight: { xs: 1.2, sm: 1.4 }
										}}
									>
										{cardItem.title}
									</Typography>

									<Typography
										fontWeight={600}
										sx={{
											fontSize: { xs: "1.25rem", sm: "1.5rem" },
											lineHeight: { xs: 1.2, sm: 1.4 }
										}}
									>
										{cardItem.value}
									</Typography>
								</Box>
							</Box>
						)}
					</Grid>
				))}
			</Grid>

			<Box
				className="authorized-list-wrapper"
				sx={{
					background: "#ffffff",
					padding: { xs: "0.75rem", sm: "1rem" },
					borderRadius: "12px",
					flexGrow: { xs: 0, sm: 1 },
					display: "flex",
					flexDirection: "column",
					gap: { xs: "0.75rem", sm: "1rem" },
					boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
					border: "1px solid rgba(0, 0, 0, 0.06)",
					// On mobile, don't constrain height to allow natural flow
					height: { xs: "auto", sm: "auto" },
					minHeight: { xs: "auto", sm: 0 }
				}}
			>
				<Box
					className="authorized-list-header-wrapper"
					sx={{
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "stretch", sm: "center" },
						justifyContent: "space-between",
						gap: { xs: "0.75rem", sm: "1rem" }
					}}
				>
					{/* Mobile: Row 1 - Search Input (Full Width) */}
					<Box
						sx={{
							display: { xs: "block", sm: "none" },
							width: "100%"
						}}
					>
						<Box component="form" noValidate onSubmit={handleSearchAuthorizedListVehicle}>
							<TextField
								variant="filled"
								placeholder="Search"
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								// classes={{ root: "vehicle-number-input-root" }}
								sx={{
									width: "100%",
									"& .MuiInputBase-input": {
										fontSize: "0.875rem"
									}
								}}
								autoComplete="off"
								inputRef={searchInputRef}
								InputProps={{
									disableUnderline: true,
									hiddenLabel: true,
									classes: { root: "filled-input-root dashboard-search-input" },
									startAdornment: (
										<InputAdornment position="start">
											<IconButton edge="start" size="small" type="submit" color="primary">
												<SearchIcon fontSize="medium" />
											</IconButton>
										</InputAdornment>
									),
									endAdornment: filters.search ? (
										<InputAdornment position="end">
											<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									) : null
								}}
							/>
						</Box>
					</Box>

					{/* Mobile: Row 2 - Download & Upload Buttons (Half-Half Widths) */}
					<Box
						sx={{
							display: { xs: "flex", sm: "none" },
							width: "100%",
							gap: "0.5rem"
						}}
					>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.UPLOAD_WHITELIST) ? (
							<Button
								variant="outlined"
								color="primary"
								startIcon={<FileUploadIcon />}
								onClick={handleOpenUploadDataDialog}
								sx={{
									fontSize: "0.875rem",
									padding: "6px 12px",
									flex: 1
								}}
							>
								Upload
							</Button>
						) : null}

						<LoadingButton
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadButtonClick}
							loading={showDownloadButtonLoader}
							loadingPosition="start"
							sx={{
								fontSize: "0.875rem",
								padding: "6px 12px",
								flex: 1
							}}
						>
							Download
						</LoadingButton>
					</Box>

					{/* Mobile: Row 3 - Add Button (Full Width) */}
					<Box
						sx={{
							display: { xs: "block", sm: "none" },
							width: "100%"
						}}
					>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADD_OWNER) ? (
							<Button
								variant="contained"
								color="success"
								startIcon={<AddCircleIcon />}
								onClick={() => handleOpenAuthorizedMemberDetailsDialog(null)}
								sx={{
									fontSize: "0.875rem",
									padding: "6px 12px",
									width: "100%"
								}}
							>
								Add
							</Button>
						) : null}
					</Box>

					{/* Desktop: Original Layout */}
					<Box
						className="authorized-list-header-actions-wrapper"
						sx={{
							display: { xs: "none", sm: "flex" },
							alignItems: "center",
							justifyContent: "flex-start",
							gap: "0.75rem",
							flexWrap: "wrap"
						}}
					>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADD_OWNER) ? (
							<Button
								variant="contained"
								color="success"
								startIcon={<AddCircleIcon />}
								onClick={() => handleOpenAuthorizedMemberDetailsDialog(null)}
								sx={{
									fontSize: "1rem",
									padding: "8px 16px"
								}}
							>
								Add
							</Button>
						) : null}
					</Box>
					<Box
						className="authorized-list-header-actions-wrapper"
						sx={{
							display: { xs: "none", sm: "flex" },
							alignItems: "center",
							justifyContent: "flex-end",
							gap: "0.75rem",
							flexWrap: "wrap"
						}}
					>
						<Box component="form" noValidate onSubmit={handleSearchAuthorizedListVehicle}>
							<TextField
								variant="filled"
								placeholder="Search using number plate"
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								// classes={{ root: "vehicle-number-input-root" }}
								sx={{
									width: "300px",
									"& .MuiInputBase-input": {
										fontSize: "1rem"
									}
								}}
								autoComplete="off"
								inputRef={searchInputRef}
								InputProps={{
									disableUnderline: true,
									hiddenLabel: true,
									classes: { root: "filled-input-root dashboard-search-input" },
									startAdornment: (
										<InputAdornment position="start">
											<IconButton edge="start" size="small" type="submit" color="primary">
												<SearchIcon fontSize="medium" />
											</IconButton>
										</InputAdornment>
									),
									endAdornment: filters.search ? (
										<InputAdornment position="end">
											<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									) : null
								}}
							/>
						</Box>

						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.UPLOAD_WHITELIST) ? (
							<Button
								variant="outlined"
								color="primary"
								startIcon={<FileUploadIcon />}
								onClick={handleOpenUploadDataDialog}
								sx={{
									fontSize: "1rem",
									padding: "8px 16px"
								}}
							>
								Upload
							</Button>
						) : null}

						<LoadingButton
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadButtonClick}
							loading={showDownloadButtonLoader}
							loadingPosition="start"
							sx={{
								fontSize: "1rem",
								padding: "8px 16px"
							}}
						>
							Download
						</LoadingButton>
					</Box>
				</Box>

				<Box
					className="authorized-list-table-wrapper"
					sx={{
						// On mobile, use auto height instead of flex-grow to allow natural flow
						flexGrow: { xs: 0, sm: 1 },
						background: "#f8f9fa",
						// Remove height constraint on mobile to allow natural flow
						height: { xs: "auto", sm: 0 },
						display: "flex",
						flexDirection: "column",
						// Set minimum height for mobile to ensure table is visible
						minHeight: { xs: "400px", sm: "auto" }
					}}
				>
					<DataGrid
						columns={columnsData}
						rows={getAuthorizedListRowData(authorizedListData?.results ?? [])}
						getRowId={(item) => item.id}
						rowSelectionModel={selectedWhitelistIds}
						onRowSelectionModelChange={handleSelectRows}
						loading={getAuthorizedListLoading || getAuthorizedListFetching}
						disableColumnMenu
						disableRowSelectionOnClick
						density="compact"
						rowSpacingType="border"
						columnHeaderHeight={65}
						paginationMode="server"
						pageSizeOptions={[ROWS_PER_PAGE]}
						onPaginationModelChange={handlePageChange}
						rowCount={authorizedListData?.count ?? 0}
						paginationModel={{ page: filters.page ? filters.page - 1 : 0, pageSize: ROWS_PER_PAGE }}
						slots={{ pagination: AuthorizedListPagination }}
						slotProps={{ footer: { sx: { justifyContent: "center" } } }}
						hideFooter={(authorizedListData?.count ?? 0) <= ROWS_PER_PAGE}
						getRowHeight={({ densityFactor, model }: GridRowHeightParams) => {
							if (model.vehicles && model.vehicles.length > 1) {
								return 50 * model.vehicles.length * densityFactor;
							}

							return null;
						}}
						classes={{
							columnSeparator: "authorized-list-table-column-separator",
							columnHeader: "authorized-list-table-column-headers",
							cell: "authorized-list-table-cell",
							root: "authorized-list-table-root"
						}}
						sx={{
							"& .MuiDataGrid-cell": {
								fontSize: { xs: "0.75rem", sm: "0.875rem" }
							},
							"& .MuiDataGrid-columnHeader": {
								fontSize: { xs: "0.75rem", sm: "0.875rem" }
							},
							// On mobile, set a fixed height for the DataGrid to prevent overflow
							height: { xs: "400px", sm: "auto" }
						}}
					/>
				</Box>
			</Box>

			<AuthorizedMemberDetailsDialog
				open={showAuthorizedMemberDetailsDialog}
				memberDetails={selectedMemberDetails}
				onClose={handleCloseAuthorizedMemberDetailsDialog}
			/>

			<NewUploadDataDialog
				open={showUploadDataDialog}
				sampleFileUrl="/analytics/download/members_info_template"
				uploadButtonLoading={uploadDataLoading}
				onClose={handleCloseUploadDataDialog}
				onUploadButtonClick={handleUploadAuthorizedListDetails}
			/>
		</Box>
	);
};

export default AuthorizedList;
